/**
 * Shared validation utilities for group management forms
 */
import { ALLOWED_LEVERAGE, CATEGORY_LEVERAGE_LIMITS, isAllowedLeverage } from './constants';

/**
 * Validate group form data
 * @param {Object} formData - The form data to validate
 * @returns {Object} - Object containing validation errors
 */
export const validateGroupForm = (formData) => {
  const errors = {};
  
  if (!formData.name?.trim()) {
    errors.name = 'Group name is required';
  } else if (formData.name.length < 3) {
    errors.name = 'Group name must be at least 3 characters';
  } else if (formData.name.length > 50) {
    errors.name = 'Group name must be less than 50 characters';
  }

  if (formData.description && formData.description.length > 500) {
    errors.description = 'Description must be less than 500 characters';
  }

  if (formData.commission < 0 || formData.commission > 0.1) {
    errors.commission = 'Commission must be between 0 and 10%';
  }

  if (formData.swap < 0 || formData.swap > 0.1) {
    errors.swap = 'Swap must be between 0 and 10%';
  }

  if (formData.swapEnabled && formData.swapTime) {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(formData.swapTime)) {
      errors.swapTime = 'Swap time must be in HH:mm format (UTC)';
    }
  }

  if (formData.tripleSwapDay && !['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].includes(formData.tripleSwapDay)) {
    errors.tripleSwapDay = 'Invalid triple swap day';
  }

  if (formData.defaultSpread < 0 || formData.defaultSpread > 0.01) {
    errors.defaultSpread = 'Default spread must be between 0 and 1%';
  }

  if (formData.maxPositionSize <= 0 || formData.maxPositionSize > 10000000) {
    errors.maxPositionSize = 'Max position size must be between $1 and $10,000,000';
  }

  // Validate leverage settings - both category-specific and global
  if (formData.leverageSettings) {
    Object.entries(formData.leverageSettings).forEach(([category, settings]) => {
      // Validate category-specific leverage
      if (typeof settings === 'object' && CATEGORY_LEVERAGE_LIMITS[category]) {
        const maxLimit = CATEGORY_LEVERAGE_LIMITS[category];
        
        // Validate max leverage
        if (settings.maxLeverage <= 0 || !Number.isInteger(settings.maxLeverage)) {
          errors[`leverage_${category}_maxLeverage`] = `${category.charAt(0).toUpperCase() + category.slice(1)} max leverage must be a positive integer`;
        } else if (!isAllowedLeverage(settings.maxLeverage)) {
          errors[`leverage_${category}_maxLeverage`] = `${category.charAt(0).toUpperCase() + category.slice(1)} max leverage must be one of the allowed values: ${ALLOWED_LEVERAGE.join(', ')}`;
        } else if (settings.maxLeverage > maxLimit) {
          errors[`leverage_${category}_maxLeverage`] = `${category.charAt(0).toUpperCase() + category.slice(1)} max leverage must not exceed category limit of ${maxLimit}:1`;
        }
        
        // Validate current leverage
        if (settings.currentLeverage <= 0 || !Number.isInteger(settings.currentLeverage)) {
          errors[`leverage_${category}_currentLeverage`] = `${category.charAt(0).toUpperCase() + category.slice(1)} current leverage must be a positive integer`;
        } else if (!isAllowedLeverage(settings.currentLeverage)) {
          errors[`leverage_${category}_currentLeverage`] = `${category.charAt(0).toUpperCase() + category.slice(1)} current leverage must be one of the allowed values: ${ALLOWED_LEVERAGE.join(', ')}`;
        } else if (settings.currentLeverage > settings.maxLeverage) {
          errors[`leverage_${category}_currentLeverage`] = `${category.charAt(0).toUpperCase() + category.slice(1)} current leverage must not exceed max leverage`;
        }
      }
      
      // Validate global leverage settings
      if (category === 'globalMaxLeverage') {
        if (typeof settings !== 'number' || settings <= 0 || !Number.isInteger(settings)) {
          errors.globalMaxLeverage = 'Global max leverage must be a positive integer';
        } else if (!isAllowedLeverage(settings)) {
          errors.globalMaxLeverage = `Global max leverage must be one of the allowed values: ${ALLOWED_LEVERAGE.join(', ')}`;
        }
      }
      
      if (category === 'globalCurrentLeverage') {
        const globalMax = formData.leverageSettings.globalMaxLeverage || 500;
        if (typeof settings !== 'number' || settings <= 0 || !Number.isInteger(settings)) {
          errors.globalCurrentLeverage = 'Global current leverage must be a positive integer';
        } else if (!isAllowedLeverage(settings)) {
          errors.globalCurrentLeverage = `Global current leverage must be one of the allowed values: ${ALLOWED_LEVERAGE.join(', ')}`;
        } else if (settings > globalMax) {
          errors.globalCurrentLeverage = 'Global current leverage must not exceed global max leverage';
        }
      }
    });
  }

  return errors;
};

/**
 * Get initial form data with default values
 * @returns {Object} - Initial form data
 */
export const getInitialFormData = () => ({
  name: '',
  description: '',
  leverageSettings: {
    forex: { maxLeverage: 500, currentLeverage: 200 },
    stocks: { maxLeverage: 50, currentLeverage: 10 },
    crypto: { maxLeverage: 100, currentLeverage: 30 },
    indices: { maxLeverage: 200, currentLeverage: 75 },
    commodities: { maxLeverage: 100, currentLeverage: 25 },
    globalMaxLeverage: 500,
    globalCurrentLeverage: 500
  },
  commission: 0.0005,
  swap: 0.001,
  swapEnabled: false,
  swapTime: '22:00',
  tripleSwapDay: 'Wednesday',
  defaultSpread: 0.00005,
  maxPositionSize: 500000,
  allowedInstruments: []
});

/**
 * Check if form has errors
 * @param {Object} errors - Validation errors object
 * @returns {boolean} - True if there are errors
 */
export const hasFormErrors = (errors) => {
  return Object.keys(errors).length > 0;
};